import React from 'react';
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from '../../hooks/useThemeColor';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import {
  LocalizationProvider,
  DateTimePicker,
  DatePicker,
  TimePicker,
} from '@mui/x-date-pickers';
import moment from 'moment';

const CustomDateTimePicker = ({
  label,
  value,
  onChange,
  disabled = false,
  isValidDate,
  placeholder = 'Select',
  sx,
  ampm = false,
  mode = 'datetime',
  inputProps = {},
  slotProps: userSlotProps,
  ...props
}) => {
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const parseColorToRGB = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r}, ${g}, ${b}`;
  };

  /* MUI X v8+ uses DateTimeField + PickersTextField; `renderInput` is ignored. */
  const pickerFieldSx = {
    '& .MuiPickersTextField-root': {
      color: isDarkMode ? '#ffffff' : '#000000',
    },
    '& .MuiPickersInputBase-root': {
      height: '44px',
      borderRadius: '10px',
      border: isDarkMode ? '1px solid #404040 !important' : '1px solid #D9D9D9 !important',
      backgroundColor: isDarkMode ? '#2d3436 !important' : '#F9F9F9 !important',
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        borderColor: isDarkMode ? '#555 !important' : '#bbb !important',
      },
      '&.Mui-focused': {
        borderColor: `${themeColor} !important`,
        boxShadow: `0 0 0 2px rgba(${parseColorToRGB(themeColor)}, 0.25) !important`,
      },
    },
    '& .MuiPickersOutlinedInput-root': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
    },
    '& .MuiPickersInputBase-sectionsContainer': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
    },
    '& .MuiPickersInputBase-sectionContent': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
    },
    '& .MuiPickersSectionList-root': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
    },
    '& .MuiPickersOutlinedInput-sectionsContainer': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
    },
    '& .MuiPickersSection-root': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
    },
    '& .MuiPickersInputBase-section': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
    },
    '& .MuiInputBase-root': {
      height: '44px',
      borderRadius: '10px',
      border: isDarkMode ? '1px solid #404040 !important' : '1px solid #D9D9D9 !important',
      backgroundColor: isDarkMode ? '#2d3436 !important' : '#F9F9F9 !important',
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        borderColor: isDarkMode ? '#555 !important' : '#bbb !important',
      },
      '&.Mui-focused': {
        borderColor: `${themeColor} !important`,
        boxShadow: `0 0 0 2px rgba(${parseColorToRGB(themeColor)}, 0.25) !important`,
      },
    },
    '& .MuiInputBase-input': {
      padding: '0 14px',
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
      WebkitTextFillColor: isDarkMode ? '#ffffff !important' : '#000000 !important',
      caretColor: isDarkMode ? '#ffffff' : '#000000',
      opacity: 1,
      fontSize: '14px',
      fontWeight: '400',
      '&::placeholder': isDarkMode
        ? {
            color: '#ffffff !important',
            WebkitTextFillColor: '#ffffff !important',
            opacity: '1 !important',
          }
        : {
            color: '#6c757d !important',
            opacity: '1 !important',
          },
    },
    '& input': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
      WebkitTextFillColor: isDarkMode ? '#ffffff !important' : '#000000 !important',
      caretColor: isDarkMode ? '#ffffff' : '#000000',
      opacity: '1 !important',
    },
    '& input::placeholder': isDarkMode
      ? {
          color: '#ffffff !important',
          WebkitTextFillColor: '#ffffff !important',
          opacity: '1 !important',
        }
      : {
          color: '#6c757d !important',
          opacity: '1 !important',
        },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? '#ffffff !important' : '#000000 !important',
      fontSize: '14px',
      '&.Mui-focused': {
        color: isDarkMode ? '#ffffff !important' : '#000000 !important',
      },
      '&.Mui-disabled': {
        color: isDarkMode ? '#666 !important' : '#999 !important',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '& .MuiInputAdornment-root': {
      color: isDarkMode ? '#ffffff !important' : '#6c757d !important',
    },
    '& .MuiIconButton-root': {
      color: isDarkMode ? '#ffffff !important' : '#6c757d !important',
      '&:hover': {
        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1) !important' : 'rgba(0, 0, 0, 0.04) !important',
      },
    },
    '& .MuiSvgIcon-root': {
      color: isDarkMode ? '#ffffff !important' : '#6c757d !important',
    },
    '&.Mui-disabled': {
      '& .MuiInputBase-root, & .MuiPickersInputBase-root': {
        backgroundColor: isDarkMode ? '#1e2022 !important' : '#e9ecef !important',
        color: isDarkMode ? '#666 !important' : '#6c757d !important',
        borderColor: isDarkMode ? '#333 !important' : '#ced4da !important',
      },
      '& .MuiInputBase-input': {
        color: isDarkMode ? '#666 !important' : '#6c757d !important',
        WebkitTextFillColor: isDarkMode ? '#666 !important' : '#6c757d !important',
      },
    },
  };

  const userTf = userSlotProps?.textField || {};
  const userTfSx = userTf.sx;
  const extraSx = Array.isArray(userTfSx) ? userTfSx : userTfSx != null ? [userTfSx] : [];

  const mergedSlotProps = {
    ...userSlotProps,
    textField: {
      placeholder,
      ...userTf,
      InputProps: {
        ...userTf.InputProps,
        ...inputProps,
        className: [
          userTf.InputProps?.className,
          inputProps?.className,
          isDarkMode ? 'dark-mode-input' : 'light-mode-input',
        ]
          .filter(Boolean)
          .join(' '),
      },
      sx: [pickerFieldSx, sx, ...extraSx],
    },
  };

  const commonProps = {
    label,
    value: value ? moment(value) : null,
    onChange: (newValue) => onChange(newValue ? newValue.toDate() : null),
    disabled,
    shouldDisableDate: isValidDate,
    ampm,
    slotProps: mergedSlotProps,
    ...props,
  };

  const getPickerComponent = () => {
    switch (mode) {
      case 'date':
        return <DatePicker {...commonProps} />;
      case 'time':
        return <TimePicker {...commonProps} />;
      default:
        return <DateTimePicker {...commonProps} />;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      {getPickerComponent()}
    </LocalizationProvider>
  );
};

export default CustomDateTimePicker;
